import { authentication, dataProvider, functionProvider } from '@modular-rest/client';
import { toastError } from 'pilotui/toast';
import { defineStore } from 'pinia';

import { COLLECTIONS, DATABASE, type FreemiumAllocationType, type ProfileType, type SubscriptionType } from '~/types/database.type';
import { isDeadSessionError } from '~/utils/deadSession';
import { identifyAnalyticsUser, resetAnalyticsUser } from '~/plugins/mixpanel';


export const useProfileStore = defineStore('profile', () => {
    const authUser = computed(() => authentication.user);
    const isLogin = computed(() => authentication.isLogin);

    // profile
    const userDetail = ref<ProfileType>();
    const profilePictureBase64 = ref<string>('');
    const profilePicture = computed(() => profilePictureBase64.value || userDetail.value?.gPicture || '');
    const email = computed(() => authUser.value?.email);

    const PICTURE_CACHE_PREFIX = 'profile_picture_b64:';
    const PICTURE_FAIL_PREFIX = 'profile_picture_fail:';
    const PICTURE_FAIL_TTL_MS = 24 * 60 * 60 * 1000;
    const CACHE_RESIZE_PX = 96;
    const CACHE_JPEG_QUALITY = 0.85;

    type PictureCacheEntry = { url: string; dataUri: string };
    type PictureFailEntry = { url: string; ts: number };

    function pictureCacheKey(userId: string) {
        return `${PICTURE_CACHE_PREFIX}${userId}`;
    }

    function pictureFailKey(userId: string) {
        return `${PICTURE_FAIL_PREFIX}${userId}`;
    }

    function readCachedPicture(userId: string): PictureCacheEntry | null {
        try {
            const raw = localStorage.getItem(pictureCacheKey(userId));
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed.url === 'string' && typeof parsed.dataUri === 'string') {
                return parsed;
            }
        } catch {
            // corrupt entry — fall through to null
        }
        return null;
    }

    function writeCachedPicture(userId: string, entry: PictureCacheEntry) {
        try {
            localStorage.setItem(pictureCacheKey(userId), JSON.stringify(entry));
        } catch {
            // QuotaExceededError — leave ref populated for the session, skip persistence
        }
    }

    function isPictureMarkedFailed(userId: string, url: string): boolean {
        try {
            const raw = localStorage.getItem(pictureFailKey(userId));
            if (!raw) return false;
            const parsed = JSON.parse(raw) as PictureFailEntry;
            if (parsed?.url !== url) return false;
            if (Date.now() - parsed.ts > PICTURE_FAIL_TTL_MS) {
                localStorage.removeItem(pictureFailKey(userId));
                return false;
            }
            return true;
        } catch {
            return false;
        }
    }

    function markPictureFailed(userId: string, url: string) {
        try {
            localStorage.setItem(pictureFailKey(userId), JSON.stringify({ url, ts: Date.now() }));
        } catch {
            // ignore
        }
    }

    async function downloadAndCachePicture(userId: string, url: string): Promise<void> {
        const dataUri = await new Promise<string>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = CACHE_RESIZE_PX;
                    canvas.height = CACHE_RESIZE_PX;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return reject(new Error('Canvas 2D unavailable'));
                    ctx.drawImage(img, 0, 0, CACHE_RESIZE_PX, CACHE_RESIZE_PX);
                    resolve(canvas.toDataURL('image/jpeg', CACHE_JPEG_QUALITY));
                } catch (e) {
                    reject(e);
                }
            };
            img.onerror = () => reject(new Error('Failed to load profile image'));
            img.src = url;
        });

        profilePictureBase64.value = dataUri;
        writeCachedPicture(userId, { url, dataUri });
    }

    // subscription
    const isFreemium = ref(false);
    const isSubscriptionFetching = ref(true);
    const activeSubscription = ref<SubscriptionType | null>(null);
    const freemiumAllocation = ref<FreemiumAllocationType | null>(null);

    // Percentage of the AI budget used in the current window (0-100). Drives the
    // soft-cap banner; works for both the Starter allocation and paid subscriptions.
    const usagePercentage = computed(() => {
        if (isFreemium.value) return freemiumAllocation.value?.usage_percentage ?? 0;
        return activeSubscription.value?.usage_percentage ?? 0;
    });
    // AI features are paused once the budget is fully used (the 100% hard cap).
    const isAiPaused = computed(() => usagePercentage.value >= 100);

    function fetchSubscription() {
        isSubscriptionFetching.value = true;

        return functionProvider
            .run<SubscriptionType | FreemiumAllocationType>({
                name: 'getSubscriptionDetails',
                args: {
                    userId: authUser.value?.id,
                },
            })
            .then((res) => {
                isFreemium.value = res.is_freemium;

                if (!res.is_freemium) {
                    activeSubscription.value = res;
                } else {
                    freemiumAllocation.value = res as FreemiumAllocationType;
                }
            })
            .catch((res) => {
                // A dead session must reach loginWithLastSession, which logs out.
                if (isDeadSessionError(res)) throw res;
                console.error('Error fetching subscription:', res);
                toastError(res.error || 'Unable to fetch subscription details', { position: 'top-end' });
            })
            .finally(() => {
                isSubscriptionFetching.value = false;
            });
    }

    function logout() {
        const userId = authUser.value?.id;
        if (userId) {
            try {
                localStorage.removeItem(pictureCacheKey(userId));
                localStorage.removeItem(pictureFailKey(userId));
            } catch {
                // ignore
            }
        }
        profilePictureBase64.value = '';
        resetAnalyticsUser();
        authentication.logout();
        userDetail.value = undefined;
    }

    function getProfileInfo() {
        return dataProvider
            .findOne<ProfileType>({
                database: DATABASE.USER_CONTENT,
                collection: COLLECTIONS.PROFILE,
                query: {
                    refId: authentication.user?.id,
                },
            })
            .then((profile) => {
                const userId = authentication.user?.id;
                const gPicture = profile?.gPicture;
                const knownFailed = !!(userId && gPicture && isPictureMarkedFailed(userId, gPicture));

                // `knownFailed` means the CANVAS CACHE of this URL failed before, not that the URL
                // is undisplayable — see the note in the catch below. So the profile is exposed
                // with gPicture intact either way, and only the re-encode is skipped; StAvatar
                // falls back to initials if the <img> itself actually errors.
                userDetail.value = profile;

                if (userId && gPicture && !knownFailed) {
                    const cached = readCachedPicture(userId);
                    if (cached && cached.url === gPicture) {
                        profilePictureBase64.value = cached.dataUri;
                    } else {
                        // No cache or URL drifted — clear stale ref, fire-and-forget re-download.
                        // UI falls back to gPicture URL until the encode completes.
                        profilePictureBase64.value = '';
                        downloadAndCachePicture(userId, gPicture).catch(() => {
                            // The caching fetch failed — remember that, so we don't re-attempt the
                            // encode on every load.
                            //
                            // But do NOT strip gPicture. This path draws the image to a canvas and
                            // calls toDataURL(), which is the only reason it needs `crossOrigin`,
                            // and so the usual failure here is a CORS one. A plain <img src> needs
                            // no CORS whatsoever — the browser renders that URL fine. Dropping
                            // gPicture on a CORS error therefore hid a perfectly displayable
                            // avatar behind the initials fallback. A genuinely dead URL is caught
                            // where it belongs, by the <img> itself: StAvatar falls back to
                            // initials on its error event.
                            markPictureFailed(userId, gPicture);
                        });
                    }
                } else {
                    // No gPicture (or we just stripped it) — drop any stale cached base64 so
                    // ProfileButton falls through to its placeholder.
                    profilePictureBase64.value = '';
                    if (userId && !gPicture) {
                        try { localStorage.removeItem(pictureCacheKey(userId)); } catch {}
                    }
                }

                return profile;
            })
            .catch((error) => {
                if (isDeadSessionError(error)) throw error;
                toastError(error.error || 'Unable to fetch profile info', { position: 'top-end' });
            });
    }

    // bootstrap the profile store
    function bootstrap() {
        // Every path into a signed-in app runs through here (session restore and
        // explicit login), so this is where analytics learns who the user is.
        identifyAnalyticsUser(authentication.user);
        return Promise.all([
            // Fetch profile info
            getProfileInfo(),
            // Fetch subscription details
            fetchSubscription(),
        ]);
    }

    function loginWithLastSession() {
        return authentication
            .loginWithLastSession()
            .then(() => {
                // Only fetch protected user data when this is a real user session.
                // Anonymous tokens may legitimately be in localStorage (other tabs,
                // login screen priming) — don't logout/clear them, just skip bootstrap.
                if (authentication.user?.type !== 'user') {
                    return null;
                }
                const userToken = authentication.getToken;
                return bootstrap()
                    .then(() => {
                        // Defense-in-depth: re-save the user token after bootstrap.
                        // External actors (chrome extension content scripts, other
                        // tabs mid-anonymous flow) can overwrite localStorage.token
                        // between saveSession and bootstrap completion.
                        if (userToken && authentication.user?.type === 'user') {
                            localStorage.setItem('token', userToken);
                        }
                    })
                    .catch((error) => {
                        const message = (error?.error || error?.message || '').toString().toLowerCase();
                        if (isDeadSessionError(error) || message.includes('authentication')) {
                            authentication.logout();
                        }
                        throw error;
                    });
            })
            .catch(() => null);
    }

    function updateProfile(profileData: { name?: string; profileImage?: File; preferences?: Record<string, boolean>; timeZone?: string }) {
        const updatePayload: any = {};
        if (profileData.name) updatePayload.name = profileData.name;
        if (profileData.timeZone) updatePayload.timeZone = profileData.timeZone;

        if (Object.keys(updatePayload).length === 0) {
            return Promise.resolve(userDetail.value);
        }

        return dataProvider
            .updateOne({
                database: DATABASE.USER_CONTENT,
                collection: COLLECTIONS.PROFILE,
                query: {
                    refId: authentication.user?.id,
                },
                update: updatePayload,
            })
            .then((res) => {
                if (userDetail.value) {
                    if (profileData.name) userDetail.value.name = profileData.name;
                    if (profileData.timeZone) userDetail.value.timeZone = profileData.timeZone;
                }
                return res;
            });
    }

    return {
        authUser,
        userDetail,
        isLogin,
        profilePicture,
        email,

        activeSubscription,
        isSubscriptionFetching,
        isFreemium,
        freemiumAllocation,
        usagePercentage,
        isAiPaused,
        fetchSubscription,

        logout,
        getProfileInfo,
        loginWithLastSession,
        bootstrap,
        updateProfile,
    };
});
